import {
  base_colorAction,
  base_colorSurfaceSecondary,
  cta_linkTextAlert,
  input_backgroundDefault,
  input_borderActive,
  input_borderError,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { css } from 'styled-components'

export const title = css`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing(3)};
`

// Wraps the trigger button and the tooltip; relative so it anchors the
// absolutely-positioned HoverTooltip. The tooltip lives here (a sibling of the
// button) rather than inside the button so its text never joins the button's
// accessible name.
export const editTriggerWrap = css`
  position: relative;
  display: inline-flex;
  align-items: center;
`

// The whole title doubles as the edit trigger: hovering reveals a tooltip and
// clicking (the name or the pencil) opens the editor — mirrors the inline name
// edit on the reimbursement report details screen.
export const editTrigger = css`
  font: inherit;
  color: inherit;
  display: inline-flex;
  align-items: center;
  gap: ${spacing(2)};
  // Expand the hit area, then pull it back so the title stays aligned with the
  // surrounding page chrome.
  padding: ${spacing(1)} ${spacing(2)};
  margin: 0 -${spacing(2)};
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  &:hover {
    background-color: ${base_colorSurfaceSecondary};
  }
  // Match the app convention: suppress the ring on mouse focus and show a
  // prominent 2px outline only for keyboard focus via :focus-visible.
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
  }
`

export const editIcon = css`
  flex-shrink: 0;
`

// Keeps the input and the (now persistent) pencil grouped while editing, with
// the same gap the non-editing trigger uses so the icon doesn't shift.
export const editRow = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing(2)};
`

export const nameInput = (hasError: boolean) => css`
  font: inherit;
  box-sizing: border-box;
  // Width tracks the text via the size attribute; cap it so a long name can't
  // blow past the page chrome.
  width: auto;
  max-width: 100%;
  color: ${text_colorTextPrimary};
  background-color: ${input_backgroundDefault};
  border: 1px solid
    ${hasError ? input_borderError : input_borderActive};
  border-radius: 4px;
  padding: ${spacing(1)} ${spacing(2)};
  // Match the app convention (e.g. Accordion, CardDisplayBox): suppress the
  // ring on mouse focus and show a prominent 2px outline only for keyboard
  // focus via :focus-visible.
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid
      ${hasError ? input_borderError : base_colorAction};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.6;
  }
`

// Sits on its own wrapped line below the title (flex-basis: 100%) so it reserves
// vertical space instead of overlapping the page subtitle. The explicit
// line-height overrides the tall heading line-height it would otherwise inherit
// from the LayoutPage <h1>, which is what bloated the gap under the input.
export const helperText = (hasError: boolean) => css`
  flex-basis: 100%;
  margin-top: -${spacing(1)};
  line-height: 1.2;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 400;
  color: ${hasError
    ? cta_linkTextAlert
    : text_colorTextSecondary};
`
